import { useBreakpoint } from '../hooks/useBreakpoint';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { remindersAPI } from '../services/api';
import CommandPalette, { rememberPage } from './experience/CommandPalette';
import AnimatedIcon from './experience/AnimatedIcon';
import useTilt3D from '../hooks/useTilt3D';

const Icon = ({ name, style }) => (
  <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', verticalAlign: 'middle', ...style }}>{name}</span>
);

/* ── Notifications Panel ─────────────────────────────── */
const NotificationsPanel = ({ onClose }) => {
  const { isMobile, width: vw } = useBreakpoint();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0,10);
    remindersAPI.getAll({ upcoming: true })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data
          : Array.isArray(res.data?.data) ? res.data.data : [];
        // Show reminders due today or overdue, not dismissed
        const relevant = data.filter(r => !r.dismissed && r.due_date <= today)
          .sort((a,b) => a.due_date.localeCompare(b.due_date))
          .slice(0, 8);
        setReminders(relevant);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0,10);

  return (
    <div ref={ref} style={{
      position:'absolute', top:'calc(100% + 8px)', right:0, zIndex:200,
      background:'var(--surface-container-lowest)', border:'1px solid var(--outline-variant)',
      borderRadius:'0.875rem', boxShadow:'0 8px 32px rgba(0,0,0,0.14)',
      width: Math.min(340, vw - 16), maxHeight:460, overflow:'hidden', display:'flex', flexDirection:'column',
    }}>
      <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid var(--outline-variant)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <p style={{ fontWeight:700, fontSize:'0.9375rem', color:'var(--on-surface)' }}>Notifications</p>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--on-surface-variant)' }}>
          <Icon name="close" style={{ fontSize:'1rem' }} />
        </button>
      </div>

      <div style={{ overflowY:'auto', flex:1 }}>
        {loading && <p style={{ textAlign:'center', padding:'2rem', color:'var(--on-surface-variant)', fontSize:'0.875rem' }}>Loading…</p>}
        {!loading && reminders.length === 0 && (
          <div style={{ textAlign:'center', padding:'2.5rem 1rem', color:'var(--on-surface-variant)' }}>
            <Icon name="notifications_none" style={{ fontSize:'2.5rem', display:'block', margin:'0 auto 0.5rem', opacity:0.3 }} />
            <p style={{ fontSize:'0.875rem', fontWeight:500 }}>All caught up!</p>
            <p style={{ fontSize:'0.8125rem', opacity:0.7 }}>No pending reminders</p>
          </div>
        )}
        {reminders.map(r => {
          const isOverdue = r.due_date < today;
          return (
            <div key={r.id} style={{ padding:'0.875rem 1.25rem', borderBottom:'1px solid var(--surface-container)', display:'flex', gap:'0.75rem', alignItems:'flex-start' }}>
              <div style={{ width:36, height:36, borderRadius:'50%', background: isOverdue ? 'var(--error-container)' : 'rgba(68,104,176,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon name="alarm" style={{ fontSize:'1rem', color: isOverdue ? 'var(--error)' : 'var(--primary)' }} />
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:'0.875rem', fontWeight:600, color:'var(--on-surface)', marginBottom:'0.2rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.title}</p>
                <p style={{ fontSize:'0.75rem', color: isOverdue ? 'var(--error)' : 'var(--on-surface-variant)', fontWeight: isOverdue ? 600 : 400 }}>
                  {isOverdue ? '⚠ Overdue · ' : ''}{r.due_date}{r.due_time ? ' at ' + r.due_time.slice(0,5) : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding:'0.75rem 1.25rem', borderTop:'1px solid var(--outline-variant)' }}>
        <a href="/sales/tasks?tab=reminders" style={{ fontSize:'0.8125rem', color:'var(--primary)', fontWeight:600, textDecoration:'none' }}>
          View all reminders →
        </a>
      </div>
    </div>
  );
};

/* ── Help Panel ──────────────────────────────────────── */
const HelpPanel = ({ onClose }) => {
  const { width: vw } = useBreakpoint();
  const ref = useRef();
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const items = [
    { icon:'person_add',    label:'Add a Lead',          href:'/sales/leads' },
    { icon:'upload_file',   label:'Import Leads (CSV)',   href:'/sales/data?tab=import' },
    { icon:'task_alt',      label:'Create a Task',        href:'/sales/tasks' },
    { icon:'alarm',         label:'Set a Reminder',       href:'/sales/tasks?tab=reminders' },
    { icon:'group',         label:'Add a Candidate',      href:'/recruitment/candidates' },
    { icon:'work',          label:'Post a Job',           href:'/recruitment/jobs' },
    { icon:'settings',      label:'Manage Users',         href:'/settings' },
  ];

  return (
    <div ref={ref} style={{
      position:'absolute', top:'calc(100% + 8px)', right:0, zIndex:200,
      background:'var(--surface-container-lowest)', border:'1px solid var(--outline-variant)',
      borderRadius:'0.875rem', boxShadow:'0 8px 32px rgba(0,0,0,0.14)', width: Math.min(260, vw - 16),
    }}>
      <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid var(--outline-variant)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <p style={{ fontWeight:700, fontSize:'0.9375rem', color:'var(--on-surface)' }}>Quick Help</p>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--on-surface-variant)' }}>
          <Icon name="close" style={{ fontSize:'1rem' }} />
        </button>
      </div>
      <div style={{ padding:'0.5rem 0' }}>
        {items.map(item => (
          <a key={item.label} href={item.href} onClick={onClose} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.625rem 1.25rem', textDecoration:'none', color:'var(--on-surface)', fontSize:'0.875rem', transition:'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background='var(--surface-container-low)'}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
            <Icon name={item.icon} style={{ fontSize:'1.1rem', color:'var(--primary)' }} />
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );
};

/* ── Top Bar ─────────────────────────────────────────── */
const TopBar = ({ onMenuClick, onOpenPalette }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const isRecruit = location.pathname.startsWith('/recruitment');

  const [search, setSearch]       = useState('');
  const [showNotifs, setNotifs]   = useState(false);
  const [showHelp, setHelp]       = useState(false);

  const handleSearch = (e) => {
    if (e.key === 'Enter' && search.trim()) {
      const q = encodeURIComponent(search.trim());
      navigate(isRecruit ? `/recruitment/candidates?q=${q}` : `/sales/leads?q=${q}`);
      setSearch('');
    }
  };

  return (
    <header className="topbar">
      {/* Hamburger — shown on mobile via CSS */}
      <button className="topbar-hamburger" onClick={onMenuClick} title="Open menu" aria-label="Open navigation menu">
        <span className="material-symbols-outlined" style={{ fontSize: '1.5rem' }}>menu</span>
      </button>

      {/* App title — shown on very small screens when search hidden */}
      <span className="topbar-title">JM Data Talent CRM</span>
      {/* Search */}
      <div className="search-bar" style={{ maxWidth: 340 }}>
        <Icon name="search" style={{ position:'absolute', left:'0.625rem', top:'50%', transform:'translateY(-50%)', color:'var(--on-surface-variant)', fontSize:'1.1rem' }} />
        <input
          placeholder={`Search ${isRecruit ? 'candidates, jobs' : 'leads, tasks'}… (Enter)`}
          aria-label="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleSearch}
          style={{ paddingLeft:'2.25rem' }}
        />
      </div>
      <button type="button" className="cmdk-trigger" onClick={onOpenPalette} title="Search everything, jump to a page, or ask Nexus (Ctrl+K)">
        <AnimatedIcon name="auto_awesome" play="sparkle" size="1rem" />
        <span className="cmdk-trigger-label">Ask Nexus</span>
        <span className="kbd">Ctrl K</span>
      </button>

      {/* Right Actions */}
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
        <button
          className="btn-primary"
          onClick={() => navigate(isRecruit ? '/recruitment/candidates' : '/sales/leads')}
          style={{ fontSize:'0.8125rem', padding:'0.4rem 1rem' }}
        >
          <AnimatedIcon name="add" play="pop" size="1rem" style={{ color:'#fff' }} /> <span className="topbar-quickadd-label">Quick Add</span>
        </button>

        <div style={{ width:1, height:24, background:'var(--ghost-border)', margin:'0 0.25rem' }} />

        {/* Notifications */}
        <div style={{ position:'relative' }}>
          <button className="btn-icon" title="Notifications" aria-label="Notifications" onClick={() => { setNotifs(v=>!v); setHelp(false); }}>
            <AnimatedIcon name="notifications" play="ring" />
          </button>
          {showNotifs && <NotificationsPanel onClose={() => setNotifs(false)} />}
        </div>

        {/* Help */}
        <div style={{ position:'relative' }}>
          <button className="btn-icon" title="Help" aria-label="Help" onClick={() => { setHelp(v=>!v); setNotifs(false); }}>
            <AnimatedIcon name="help" play="wiggle" />
          </button>
          {showHelp && <HelpPanel onClose={() => setHelp(false)} />}
        </div>
      </div>
    </header>
  );
};

/* ── Layout ──────────────────────────────────────────── */
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  const openSidebar  = useCallback(() => setSidebarOpen(true),  []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const openPalette  = useCallback(() => setPaletteOpen(true),  []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // 3D tilt + glare on dashboard cards (.hover-lift / [data-tilt])
  useTilt3D();

  // Remember visited pages for the palette's "Recent" group
  useEffect(() => { rememberPage(location.pathname); }, [location.pathname]);

  // Ctrl/⌘+K toggles the command palette; "/" opens it when not typing
  useEffect(() => {
    const handler = (e) => {
      const key = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === 'k') {
        e.preventDefault();
        setPaletteOpen(v => !v);
        return;
      }
      if (key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const t = e.target;
        const typing = t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName || ''));
        if (!typing) { e.preventDefault(); setPaletteOpen(true); }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close sidebar on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeSidebar(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [closeSidebar]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  return (
    <div style={{ minHeight:'100vh', backgroundColor:'var(--surface)' }}>
      {/* Overlay backdrop — blocks content, closes sidebar on tap */}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' sidebar-overlay-open' : ''}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      <TopBar onMenuClick={openSidebar} onOpenPalette={openPalette} />
      <main className="main-content">
        {children}
      </main>
      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </div>
  );
};

export default Layout;
